import BoldSmallText from '@/components/BoldSmallText';
import CustomCard from '@/components/customCard';
import SingleDatePicker from '@/components/DatePicker/SingleDatePicker';
import RegularText from '@/components/RegularText';
import TimePicker from '@/components/TimePicker';
import UpgradeTextWithTouchPoint from '@/components/UpgradeTextWithTouchPoint';
import {
  CountdownMethodEnum,
  CountdownTimerOption,
  EndBehaviorEnum,
  SessionBehaviorEnum,
  TimeCountDownFormat,
} from '@/constants/enum';
import { createDateByTimezoneOffset, formatDate } from '@/helpers/time';
import useUserPlan from '@/hooks/useUserPlan';
import settingsSlice, { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IParamsApi } from '@/types/apis/params';
import { Checkbox, ChoiceList, Divider, InlineGrid, Select, TextField, Tooltip, useMediaQuery } from '@shopify/polaris';
import { InfoIcon } from '@shopify/polaris-icons';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { countdownMode, timerFormat } from './config';
import { CountdownSettingsStyled } from './styled';

const CountdownSettings = () => {
  const { isPlanLimited } = useUserPlan();
  const dispatch = useDispatch();
  const dataSettings = useSelector(dataSettingsSelector);
  const handleChange = (key: keyof typeof dataSettings) => (value: string | number | boolean | IParamsApi.IZipcodeSetting) => {
    dispatch(
      settingsSlice.actions.handleDataSettings({
        ...dataSettings,
        [key]: value,
      }),
    );
  };

  const now = useMemo(() => {
    return createDateByTimezoneOffset(dataSettings?.date_timezone_offset);
  }, [dataSettings?.date_timezone_offset]);

  const countdownEndtime = useMemo(() => {
    return dataSettings.countdownEndtime || formatDate(now, 'YYYY-MM-DD HH:mm');
  }, [dataSettings.countdownEndtime, now]);

  const countdownUntilFinishedStartTime = useMemo(() => {
    return dataSettings.countdownUntilFinishedStartTime || formatDate(now, 'YYYY-MM-DD HH:mm');
  }, [dataSettings.countdownUntilFinishedStartTime, now]);

  const renderDateTimeSelect = (input: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
  }) => {
    const { label, value, onChange, disabled } = input;
    const [date, time] = value.split(' ');
    const [hour, minute] = time.split(':');
    return (
      <>
        <RegularText>{label}</RegularText>
        <div className="d-flex flex-wrap align-center gap-8">
          <SingleDatePicker
            date={new Date(date)}
            setDate={(newDate) => {
              onChange(formatDate(newDate, 'YYYY-MM-DD HH:mm'));
            }}
            disabledActivator={disabled}
          />
          at
          <TimePicker
            gap={'200'}
            hour={hour}
            minute={minute}
            setHour={(value) => {
              onChange(`${date} ${value}:${minute}`);
            }}
            setMinute={(value) => {
              onChange(`${date} ${hour}:${value}`);
            }}
            disabled={disabled}
          />
        </div>
      </>
    );
  };

  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <CountdownSettingsStyled>
      <CustomCard title="Countdown settings" textClassName="bold-text-styled">
        <div className="d-flex flex-column gap-16">
          <RegularText>For message text with {'{TimeCountDown}'} variable</RegularText>
          <Select
            label={<BoldSmallText>Timer format</BoldSmallText>}
            value={dataSettings.type_time_countdown.toString()}
            onChange={(value: string) => handleChange('type_time_countdown')(Number(value))}
            options={timerFormat}
          />
          <InlineGrid columns={{ xl: 4, lg: 4, md: 4, sm: 2, xs: 2 }} gap={'400'}>
            {dataSettings.type_time_countdown !== TimeCountDownFormat.HourMinute &&
              dataSettings.type_time_countdown !== TimeCountDownFormat.HourMinuteSecond && (
                <TextField label={'Day text'} autoComplete="off" value={dataSettings.days} onChange={handleChange('days')} />
              )}
            <TextField label={'Hour text'} autoComplete="off" value={dataSettings.hours} onChange={handleChange('hours')} />
            {dataSettings.type_time_countdown !== TimeCountDownFormat.DayHour && (
              <TextField
                label={'Minute text'}
                autoComplete="off"
                value={dataSettings.minutes}
                onChange={handleChange('minutes')}
              />
            )}
            {(dataSettings.type_time_countdown === TimeCountDownFormat.Full ||
              dataSettings.type_time_countdown === TimeCountDownFormat.HourMinuteSecond) && (
              <TextField
                label={'Second text'}
                autoComplete="off"
                value={dataSettings.seconds}
                onChange={handleChange('seconds')}
              />
            )}
          </InlineGrid>
          <Divider />
          <ChoiceList
            title={
              <div className="d-flex flex-column">
                <BoldSmallText>Countdown method</BoldSmallText>
                <RegularText>Configure how the countdown is calculated and displayed</RegularText>
              </div>
            }
            choices={[
              {
                label: 'Countdown until cut-off time',
                value: CountdownMethodEnum.UntilCutoff,
                helpText: 'Count until the cut-off time configured in General settings > Date and time',
                renderChildren: (isSelected) => {
                  return (
                    <div className={`${isMobile ? '' : 'mb-16'}`}>
                      {isSelected ? (
                        <div className={`mt-12 d-flex flex-wrap gap-8 align-center`}>
                          <Checkbox
                            label={<RegularText>Show timer after cut-off time, and countdown to</RegularText>}
                            checked={dataSettings.countdown_timer_show_after_cutoff}
                            onChange={handleChange('countdown_timer_show_after_cutoff')}
                          />
                          <div className="d-flex gap-8 align-center">
                            <Select
                              id="countdown_timer_option_select_box"
                              label=""
                              value={(dataSettings.countdown_timer_option || CountdownTimerOption.NextCutoff).toString()}
                              onChange={handleChange('countdown_timer_option')}
                              options={countdownMode}
                            />
                            <Tooltip content="Choose what time the timer resets to after today's cut-off is reached.">
                              <span>
                                <InfoIcon width={20} height={20} />
                              </span>
                            </Tooltip>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                },
              },
              {
                label: (
                  <div className="d-flex justify-between gap-8">
                    <RegularText>Countdown on fixed time duration</RegularText>
                    <UpgradeTextWithTouchPoint text="Pro" isVisible={isPlanLimited} />
                  </div>
                ),
                value: CountdownMethodEnum.FixedDuration,
                helpText: 'Eg. 15-minute countdown for each session',
                disabled: isPlanLimited,
                renderChildren: (isSelected) => {
                  const hoursValue = Math.floor((dataSettings.durationInSeconds || 0) / 3600);
                  const minutesValue = Math.floor(((dataSettings.durationInSeconds || 0) % 3600) / 60);
                  const secondsValue = (dataSettings.durationInSeconds || 0) % 60;
                  return (
                    <div className={`${isMobile ? '' : 'mb-16'}`}>
                      {isSelected && (
                        <div className={`mt-12 d-flex flex-column gap-16 ${isPlanLimited ? 'disable-content' : ''}`}>
                          <div className={`d-flex flex-wrap gap-8 align-center`}>
                            <div className="fixed-time-label">Time duration:</div>
                            <div className="d-flex flex-wrap gap-8 align-center">
                              <div className="inline-select-container">
                                <Select
                                  label=""
                                  value={String(hoursValue)}
                                  onChange={(value) => {
                                    const hours = parseInt(value) || 0;
                                    handleChange('durationInSeconds')(hours * 3600 + minutesValue * 60 + secondsValue);
                                  }}
                                  options={Array.from({ length: 24 }, (_, i) => ({ label: String(i), value: String(i) }))}
                                />
                                <RegularText>hours</RegularText>
                              </div>
                              <div className="inline-select-container">
                                <Select
                                  label=""
                                  value={String(minutesValue)}
                                  onChange={(value) => {
                                    const minutes = parseInt(value) || 0;
                                    handleChange('durationInSeconds')(hoursValue * 3600 + minutes * 60 + secondsValue);
                                  }}
                                  options={Array.from({ length: 60 }, (_, i) => ({ label: String(i), value: String(i) }))}
                                />
                                <RegularText>minutes</RegularText>
                              </div>
                              <div className="inline-select-container">
                                <Select
                                  label=""
                                  value={String(secondsValue)}
                                  onChange={(value) => {
                                    const seconds = parseInt(value) || 0;
                                    handleChange('durationInSeconds')(hoursValue * 3600 + minutesValue * 60 + seconds);
                                  }}
                                  options={Array.from({ length: 60 }, (_, i) => ({ label: String(i), value: String(i) }))}
                                />
                                <RegularText>seconds</RegularText>
                              </div>
                            </div>
                          </div>

                          <div className="d-flex flex-column gap-8">
                            <div className={`inline-select-container`}>
                              <label htmlFor="session-behavior-select-box" className="fixed-time-label">
                                Session behavior:
                              </label>
                              <Select
                                id="session-behavior-select-box"
                                label=""
                                options={[
                                  { label: 'Restart for each customer session', value: SessionBehaviorEnum.RestartEachSession },
                                  {
                                    label: 'Count until finished (ends at the same time for everyone)',
                                    value: SessionBehaviorEnum.CountUntilFinished,
                                  },
                                ]}
                                value={dataSettings.sessionBehavior || SessionBehaviorEnum.RestartEachSession}
                                onChange={(value) => {
                                  dispatch(
                                    settingsSlice.actions.handleDataSettings({
                                      ...dataSettings,
                                      sessionBehavior: value as SessionBehaviorEnum,
                                      countdownUntilFinishedStartTime:
                                        value === SessionBehaviorEnum.CountUntilFinished
                                          ? countdownUntilFinishedStartTime
                                          : dataSettings.countdownUntilFinishedStartTime,
                                    }),
                                  );
                                }}
                              />
                            </div>
                            {dataSettings.sessionBehavior === SessionBehaviorEnum.CountUntilFinished && (
                              <div className={`inline-select-container inline-select-container__sub-item`}>
                                {renderDateTimeSelect({
                                  label: 'Start time:',
                                  value: countdownUntilFinishedStartTime,
                                  onChange: handleChange('countdownUntilFinishedStartTime'),
                                  disabled: isPlanLimited,
                                })}
                              </div>
                            )}
                          </div>

                          <div className={`inline-select-container`}>
                            <label htmlFor="end-behavior-select-box" className="fixed-time-label">
                              When timer ends:
                            </label>
                            <Select
                              id="end-behavior-select-box"
                              label=""
                              options={[
                                { label: 'Hide the timer for that session', value: EndBehaviorEnum.Hide },
                                { label: 'Restart countdown immediately', value: EndBehaviorEnum.RestartImmediately },
                              ]}
                              value={dataSettings.endBehavior || EndBehaviorEnum.Hide}
                              onChange={handleChange('endBehavior')}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                },
              },
              {
                label: (
                  <div className="d-flex justify-between gap-8">
                    <RegularText>Countdown to specific date and time</RegularText>
                    <UpgradeTextWithTouchPoint text="Pro" isVisible={isPlanLimited} />
                  </div>
                ),
                value: CountdownMethodEnum.SpecificDatetime,
                helpText: `Eg. Countdown from visitor's current time to 18:00, June 8`,
                disabled: isPlanLimited,
                renderChildren: (isSelected) => {
                  return isSelected ? (
                    <div className={`mt-12 inline-select-container ${isPlanLimited ? 'disable-content' : ''}`}>
                      {renderDateTimeSelect({
                        label: 'From current time of session To',
                        value: countdownEndtime,
                        onChange: handleChange('countdownEndtime'),
                        disabled: isPlanLimited,
                      })}
                    </div>
                  ) : null;
                },
              },
            ]}
            selected={[dataSettings.countdownMethod || CountdownMethodEnum.UntilCutoff]}
            onChange={(value) => {
              dispatch(
                settingsSlice.actions.handleDataSettings({
                  ...dataSettings,
                  countdownMethod: value[0] as CountdownMethodEnum,
                  countdownEndtime:
                    value[0] === CountdownMethodEnum.SpecificDatetime ? countdownEndtime : dataSettings.countdownEndtime,
                }),
              );
            }}
          />
        </div>
      </CustomCard>
    </CountdownSettingsStyled>
  );
};

export default CountdownSettings;
